import { useContext } from 'react'

import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'
import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useSelector from '@hooks/useSelector'

import defaultState, { requiredFields } from '@models/brandGroupPublication'
import type { BrandGroupModel, BrandGroupPublicationModel, PublicationModel } from '@models/types'

import * as brandGroupPublicationActions from '@redux/modules/brandGroupPublication'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

export type CreateBrandGroupPublicationParams = {
  brandGroupPublicationParams: BrandGroupPublicationModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createBrandGroupPublication = (params: CreateBrandGroupPublicationParams) => {
  const { brandGroupPublicationParams, dispatch, requestOptions } = params
  const { createBrandGroupPublication: createFn } = brandGroupPublicationActions

  return dispatch(createFn(brandGroupPublicationParams, requestOptions))
}

export type DeleteBrandGroupPublicationParams = {
  brandGroupPublication: BrandGroupPublicationModel,
  dispatch: AppDispatch,
}

const deleteBrandGroupPublication = (params: DeleteBrandGroupPublicationParams) => {
  const { brandGroupPublication, dispatch } = params
  const { deleteBrandGroupPublication: deleteFn } = brandGroupPublicationActions

  return dispatch(deleteFn(brandGroupPublication))
}

export type ShowCreateBrandGroupPublicationModalPayload = {
  brandGroupPublication: Partial<BrandGroupPublicationModel>,
}

type AddBrandGroupPublicationParams = {
  brandGroupPublication: Partial<BrandGroupPublicationModel>,
  showCreateBrandGroupPublicationModal: (payload: ShowCreateBrandGroupPublicationModalPayload) => void,
}

const addBrandGroupPublication = (params: AddBrandGroupPublicationParams) => new Promise((resolve, reject) => {
  const { brandGroupPublication, showCreateBrandGroupPublicationModal } = params

  if (showCreateBrandGroupPublicationModal){
    const payload = {
      brandGroupPublication,
    }

    showCreateBrandGroupPublicationModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateBrandGroupPublicationModal not defined in PageContext callbacks'))
})

export function useBrandGroupPublicationForm(
  brandGroupPublication: Partial<BrandGroupPublicationModel> = {},
  options: UseFormOptions = {},
): UseFormPayload {
  const { validateOn } = options

  const brandGroupPublicationForm = useForm(
    defaultState,
    { entity: brandGroupPublication, requiredFields, validateOn },
    [brandGroupPublication.id, brandGroupPublication.cache_key],
  )

  return {
    ...brandGroupPublicationForm,
  }
}

export function useRelations(brandGroupPublication: BrandGroupPublicationModel) {
  const { brand_group_id, publication_id } = brandGroupPublication || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const { brandGroups, publications } = entities

  const publication: Partial<PublicationModel> = publications[publication_id] || {}
  const brandGroup: Partial<BrandGroupModel> = brandGroups[brand_group_id] || {}

  return {
    brandGroup,
    publication,
  }
}

function useBrandGroupPublication(initEntity: Partial<BrandGroupPublicationModel> = {}) {
  const { entity: brandGroupPublication } = useLatestEntity(initEntity, 'brandGroupPublications')

  const { callbacks } = useContext(PageContext)
  const {
    showCreateBrandGroupPublicationModal,
  } = callbacks || {}

  const dispatch = useThunkDispatch()

  const {
    creating, deleting, loading,
  } = useSelector(reduxState => reduxState.brandGroupPublications)

  // @ts-expect-error TS2345
  const { brandGroup, publication } = useRelations(brandGroupPublication)

  return {
    brandGroup,
    brandGroupPublication,
    callbacks: {
      addBrandGroupPublication: () => (
        addBrandGroupPublication({ brandGroupPublication, showCreateBrandGroupPublicationModal })
      ),
      createBrandGroupPublication: (
        brandGroupPublicationParams: BrandGroupPublicationModel,
        requestOptions: EntityOptions,
      ) => (
        createBrandGroupPublication({ brandGroupPublicationParams, dispatch, requestOptions })
      ),
      // @ts-expect-error TS2322
      deleteBrandGroupPublication: () => deleteBrandGroupPublication({ brandGroupPublication, dispatch }),
    },
    creating,
    deleting,
    loading,
    publication,
  }
}

export default useBrandGroupPublication
